import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { StandardEvents, CartDiscountUpdateEvent } from '@shopify/events';

/**
 * @typedef {object} DiscountTier
 * @property {number} min - Minimum quantity of eligible items, mirroring the code's own requirement.
 * @property {number} percent - Percentage off, for reference/debugging only (Shopify owns the maths).
 * @property {string} code - The Shopify discount code that encodes this tier.
 */

/**
 * Keeps the bundle tier discount code applied to the cart.
 *
 * Enforcement is a set of plain Shopify discount codes — one per tier, each with
 * its own percentage and its own minimum-quantity requirement (see
 * `docs/bundle-discount-codes.md`). This element is the piece that makes them
 * feel automatic: on every cart change it works out which tier the cart has
 * reached and applies that code, so the customer never types anything.
 *
 * Notes on the approach:
 *
 * - **Shopify is the authority, not us.** We pick a starting tier from the cart's
 *   total quantity, but each code's minimum only counts items in the collection
 *   the code is scoped to, so our count can overshoot on a mixed cart. Rather
 *   than replicate that rule client-side, we let Shopify reject the code
 *   (`applicable: false`) and step down a tier until one sticks. That stays
 *   correct even if the scoping changes in the admin.
 * - **A customer's own code always wins.** If the cart carries a code we don't
 *   manage, we leave the cart alone — silently swapping someone's promo for ours
 *   (or stacking into a rejection) is worse than skipping the bundle discount.
 * - `POST /cart/update.js` with `discount` is the same path the theme's own
 *   `cart-discount.js` uses; passing a shorter list is how codes get removed.
 *
 * @extends {Component}
 */
class BundleDiscount extends Component {
  /** Tiers, richest first, so the first match is the best the cart qualifies for. */
  /** @type {DiscountTier[]} */
  #tiers = [];

  /** Lower-cased codes this element owns; anything else is the customer's. */
  /** @type {Set<string>} */
  #managed = new Set();

  /** Guards against overlapping syncs (rapid quantity clicks, morph re-mounts). */
  #syncing = false;

  #syncRequested = false;

  /** Latest-wins token: a sync started later invalidates one still in flight. */
  #syncToken = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#tiers = this.#parseTiers();
    if (this.#tiers.length === 0) return;

    this.#managed = new Set(this.#tiers.map((tier) => tier.code.toLowerCase()));

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);

    // The cart can already hold a bundle built on an earlier page load, and the
    // tier map may have changed since the code was applied — reconcile on mount.
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    // Wait for the line change to land before asking Shopify what tier applies.
    Promise.resolve(event.promise)
      .then(() => this.#sync())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bundle-discount] cart update rejected:', error);
      });
  };

  /**
   * Reconciles the cart's discount code with the tier its quantity has earned.
   */
  async #sync() {
    if (this.#syncing) {
      this.#syncRequested = true;
      return;
    }
    this.#syncRequested = false;

    const token = ++this.#syncToken;
    this.#syncing = true;

    try {
      const cart = await this.#fetchCart();
      if (!cart || token !== this.#syncToken) return;

      const applied = this.#appliedCodes(cart);

      // Unknown code present → this is the customer's promo, not ours. Hands off.
      if (applied.some((code) => !this.#managed.has(code.toLowerCase()))) return;

      const desired = this.#tierFor(Number(cart.item_count) || 0);

      // Already exactly right — skip the write so we don't churn cart sections.
      // Compare as lists: the common case is an empty cart with no codes, and
      // that has to come out equal or every page view would POST a pointless clear.
      const current = applied.map((code) => code.toLowerCase());
      const target = desired ? [desired.code.toLowerCase()] : [];
      if (current.length === target.length && current.every((code, index) => code === target[index])) return;

      await this.#applyFrom(desired, token);
    } catch (error) {
      // A failed sync must never block shopping: the worst case is the customer
      // reaching checkout without the tier code, which the next cart change retries.
      if (error instanceof Error && error.name !== 'AbortError') {
        console.warn('[bundle-discount] failed to sync the bundle discount:', error);
      }
    } finally {
      this.#syncing = false;
      if (this.#syncRequested && this.isConnected) {
        this.#syncRequested = false;
        void this.#sync();
      }
    }
  }

  /**
   * Applies `tier`, stepping down whenever Shopify reports the code as not
   * applicable (the cart holds fewer eligible items than our count suggested).
   * Clears the code entirely once no tier is left.
   *
   * @param {DiscountTier | null} tier - Richest tier to attempt, or null to clear.
   * @param {number} token - Sync token; a newer sync abandons this one.
   */
  async #applyFrom(tier, token) {
    let index = tier ? this.#tiers.indexOf(tier) : this.#tiers.length;

    for (; index < this.#tiers.length; index++) {
      const candidate = this.#tiers[index];
      if (!candidate) break;

      const data = await this.#writeDiscount(candidate.code);
      if (token !== this.#syncToken) return;

      const entry = data?.discount_codes?.find(
        (/** @type {{ code: string, applicable?: boolean }} */ discount) =>
          discount.code?.toLowerCase() === candidate.code.toLowerCase()
      );

      // `applicable` missing → assume it stuck rather than pointlessly stepping down.
      if (!entry || entry.applicable !== false) {
        return;
      }
    }

    // Below the lowest tier (or every tier rejected): drop our code so no stale
    // one is left to fail at checkout.
    await this.#writeDiscount('');
  }

  /**
   * Sets the cart's discount codes to exactly `code` (empty string clears).
   *
   * @param {string} code
   * @returns {Promise<any>} The updated cart, including the sections to morph.
   */
  async #writeDiscount(code) {
    const sections = [...this.#cartSections().keys()];
    const deferred = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: code ? [{ code }] : [],
        promise: deferred.promise,
      })
    );

    try {
      /** @type {Record<string, unknown>} */
      const body = { discount: code, sections_url: window.location.pathname };
      if (sections.length > 0) body.sections = sections;

      const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify(body) }));
      if (!response.ok) throw new Error(`Failed to update the cart discount: ${response.status}`);

      const data = await response.json();
      await this.#recoverCartSections(data, sections);
      // Render once, then notify listeners that these sections are already current.
      await this.#render(data);
      deferred.resolve({
        cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data),
        detail: { sections: data.sections, source: 'bundle-discount', sectionsRendered: true },
      });
      return data;
    } catch (error) {
      deferred.reject(error);
      throw error;
    }
  }


  /** Recover missing HTML with a read-only request, never repeat the cart mutation. */
  async #recoverCartSections(data, ids) {
    const sections = data.sections && typeof data.sections === 'object' ? { ...data.sections } : {};
    const missing = ids.filter((id) => typeof sections[id] !== 'string' || !sections[id].trim());
    if (missing.length) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', missing.join(','));
      const response = await fetch(url.toString(), {
        headers: { Accept: '*/*' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Cart changed, but its display could not be refreshed: ' + response.status);
      const rendered = await response.json();
      for (const id of missing) {
        const html = rendered?.[id];
        if (typeof html !== 'string' || !html.includes('shopify-section-' + id)) {
          throw new Error('Cart changed, but section HTML is unavailable: ' + id);
        }
        sections[id] = html;
      }
    }
    data.sections = sections;
    return data;
  }

  /**
   * Morphs the freshly rendered cart sections so the drawer and cart page show
   * the new total and the applied-code pill.
   *
   * @param {any} data - A `/cart/update.js` response.
   */
  async #render(data) {
    const sections = data?.sections;
    if (!sections) return;

    for (const [id, mode] of this.#cartSections()) {
      const html = sections[id];
      if (typeof html === 'string') await morphSection(id, html, { mode });
    }
  }

  /**
   * Cart section ids currently on the page, mapped to the morph mode they need
   * (drawer sections hydrate, a full cart page re-renders).
   *
   * @returns {Map<string, 'hydration' | 'full'>}
   */
  #cartSections() {
    /** @type {Map<string, 'hydration' | 'full'>} */
    const sections = new Map();

    for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (!(element instanceof HTMLElement)) continue;
      const id = element.dataset.sectionId;
      if (id) sections.set(id, element.closest('theme-drawer') ? 'hydration' : 'full');
    }

    return sections;
  }

  /**
   * The richest tier the cart qualifies for, or null when it's below every tier.
   *
   * `item_count` is the whole cart, while each code counts only the items in the
   * collection it's scoped to — so this is an upper bound that `#applyFrom`
   * walks down until Shopify agrees.
   *
   * @param {number} quantity
   * @returns {DiscountTier | null}
   */
  #tierFor(quantity) {
    return this.#tiers.find((tier) => quantity >= tier.min) ?? null;
  }

  /**
   * Codes currently on the cart.
   *
   * @param {any} cart
   * @returns {string[]}
   */
  #appliedCodes(cart) {
    if (!Array.isArray(cart?.discount_codes)) return [];

    return cart.discount_codes
      .map((/** @type {{ code?: string }} */ discount) => discount?.code)
      .filter((/** @type {unknown} */ code) => typeof code === 'string' && code !== '');
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Failed to fetch the cart: ${response.status}`);
    return response.json();
  }

  /**
   * Reads the tier contract off the element, richest tier first.
   *
   * @returns {DiscountTier[]}
   */
  #parseTiers() {
    const quantities = this.#csv(this.dataset.tierQuantities);
    const percents = this.#csv(this.dataset.tierPercents);
    const codes = (this.dataset.tierCodes ?? '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);

    /** @type {DiscountTier[]} */
    const tiers = [];

    for (const [index, code] of codes.entries()) {
      const min = quantities[index];
      const percent = percents[index];
      // A tier without a quantity is unusable — skip rather than guess a minimum.
      if (!Number.isFinite(min) || min < 1) continue;
      tiers.push({ min, percent: percent ?? 0, code });
    }

    return tiers.sort((a, b) => b.min - a.min);
  }

  /**
   * @param {string | undefined} value
   * @returns {number[]}
   */
  #csv(value) {
    return (value ?? '')
      .split(',')
      .map((part) => Number(part.trim()))
      .map((number) => (Number.isFinite(number) ? number : NaN));
  }
}

if (!customElements.get('bundle-discount')) {
  customElements.define('bundle-discount', BundleDiscount);
}



